import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Tiện ích dùng chung cho các ô chọn có tìm kiếm (ComboSelect, EmployeeSelect, PartnerSelect...).
 *
 * Tìm KHÔNG DẤU kiểu tiếng Việt: gõ "dong" phải ra "Đồng", "nguyen van" ra "Nguyễn Văn A".
 * `normalize('NFD')` tách dấu thanh/dấu mũ nhưng KHÔNG tách "đ" (đó là một chữ cái riêng),
 * nên phải đổi tay đ → d.
 */
const MARKS = /[̀-ͯ]/g;

function foldChar(ch) {
  if (ch === 'đ' || ch === 'Đ') return 'd';
  return ch.normalize('NFD').replace(MARKS, '').toLowerCase();
}

/** Chuỗi đã bỏ dấu + hạ chữ thường. */
export function foldVN(str) {
  if (str == null) return '';
  return String(str).normalize('NFD').replace(MARKS, '').replace(/[đĐ]/g, 'd').toLowerCase();
}

/** Chuỗi gõ vào → các từ khoá (đã bỏ dấu). Mọi từ phải khớp (AND), thứ tự tuỳ ý. */
export function tokensOf(query) {
  return foldVN(query).split(/\s+/).filter(Boolean);
}

/**
 * Điểm khớp (số nhỏ = ưu tiên hơn); null = không khớp.
 * 0 = khớp đầu chuỗi · 1 = khớp đầu một từ · 2 = nằm giữa từ.
 */
export function matchScore(label, tokens) {
  if (!tokens.length) return 0;
  const f = foldVN(label);
  let score = 0;
  for (const t of tokens) {
    const i = f.indexOf(t);
    if (i < 0) return null;
    if (i === 0) score = Math.max(score, 0);
    else if (f[i - 1] === ' ' || f[i - 1] === '-' || f[i - 1] === '/' || f[i - 1] === '(') score = Math.max(score, 1);
    else score = Math.max(score, 2);
  }
  return score;
}

/**
 * Tách `label` thành các đoạn [{text, hit}] để tô đậm phần khớp. Làm việc trên chuỗi gốc
 * (giữ nguyên dấu) nhưng so khớp bằng chuỗi đã bỏ dấu, nên cần bảng ánh xạ vị trí.
 */
export function highlightParts(label, tokens) {
  const text = String(label ?? '');
  if (!tokens.length || !text) return [{ text, hit: false }];
  let folded = '';
  const map = [];
  for (let i = 0; i < text.length; i += 1) {
    const f = foldChar(text[i]);
    for (let k = 0; k < f.length; k += 1) { folded += f[k]; map.push(i); }
  }
  const mark = new Array(text.length).fill(false);
  tokens.forEach((t) => {
    let from = 0;
    for (;;) {
      const at = folded.indexOf(t, from);
      if (at < 0) break;
      for (let k = at; k < at + t.length; k += 1) mark[map[k]] = true;
      from = at + t.length;
    }
  });
  const parts = [];
  for (let i = 0; i < text.length; i += 1) {
    const last = parts[parts.length - 1];
    if (last && last.hit === mark[i]) last.text += text[i];
    else parts.push({ text: text[i], hit: mark[i] });
  }
  return parts;
}

/** Lọc + xếp hạng một danh sách theo ô tìm. `textOf(item)` trả chuỗi để so khớp. */
export function filterRank(items, query, textOf) {
  const tokens = tokensOf(query);
  if (!tokens.length) return items;
  const scored = [];
  items.forEach((it, idx) => {
    const s = matchScore(textOf(it), tokens);
    if (s !== null) scored.push({ it, s, idx });
  });
  scored.sort((a, b) => a.s - b.s || a.idx - b.idx);
  return scored.map((x) => x.it);
}

/**
 * Điều hướng danh sách bằng bàn phím cho ô chọn: ↑ ↓ Home End PageUp PageDown Enter Esc Tab.
 *  - count: số dòng đang hiện; isDisabled(i): dòng bị khoá (bỏ qua khi di chuyển)
 *  - onPick(i): chọn dòng i; onClose(): đóng (Esc / Tab)
 * Trả về { active, setActive, onKeyDown, itemRef(i) } — itemRef để cuộn dòng đang chọn vào tầm nhìn.
 */
export function useListKeyNav({ open, count, isDisabled, onPick, onClose, onOpen }) {
  const [active, setActive] = useState(-1);
  const els = useRef(new Map());

  const step = useCallback((from, dir) => {
    if (!count) return -1;
    let i = from;
    for (let n = 0; n < count; n += 1) {
      i += dir;
      if (i < 0 || i >= count) return from;
      if (!isDisabled || !isDisabled(i)) return i;
    }
    return from;
  }, [count, isDisabled]);

  const edge = useCallback((dir) => {
    let i = dir > 0 ? 0 : count - 1;
    while (i >= 0 && i < count && isDisabled && isDisabled(i)) i += dir;
    return i >= 0 && i < count ? i : -1;
  }, [count, isDisabled]);

  // Giữ dòng đang chọn nằm trong khoảng hợp lệ khi danh sách co lại.
  useEffect(() => {
    if (active >= count) setActive(count ? edge(1) : -1);
  }, [count, active, edge]);

  useEffect(() => {
    if (!open || active < 0) return;
    const el = els.current.get(active);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const onKeyDown = useCallback((e) => {
    if (e.nativeEvent && e.nativeEvent.isComposing) return; // đang gõ tiếng Việt (IME) — để yên
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) { if (onOpen) onOpen(); } else setActive((a) => (a < 0 ? edge(1) : step(a, 1)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) { if (onOpen) onOpen(); } else setActive((a) => (a < 0 ? edge(-1) : step(a, -1)));
        break;
      case 'PageDown':
        if (!open) return;
        e.preventDefault();
        setActive((a) => { let i = a < 0 ? edge(1) : a; for (let n = 0; n < 8; n += 1) i = step(i, 1); return i; });
        break;
      case 'PageUp':
        if (!open) return;
        e.preventDefault();
        setActive((a) => { let i = a < 0 ? edge(-1) : a; for (let n = 0; n < 8; n += 1) i = step(i, -1); return i; });
        break;
      case 'Home':
        if (!open) return;
        // Trong ô tìm, Home/End là dời con trỏ chữ — chỉ bắt khi Ctrl.
        if (e.target && e.target.tagName === 'INPUT' && !e.ctrlKey) return;
        e.preventDefault(); setActive(edge(1));
        break;
      case 'End':
        if (!open) return;
        if (e.target && e.target.tagName === 'INPUT' && !e.ctrlKey) return;
        e.preventDefault(); setActive(edge(-1));
        break;
      case 'Enter':
        if (!open) return;
        e.preventDefault();
        if (active >= 0 && active < count) onPick(active);
        break;
      case 'Escape':
        if (!open) return;
        // preventDefault để hộp thoại bao ngoài (Headless UI) thấy sự kiện đã được xử lý và KHÔNG đóng theo.
        e.preventDefault(); e.stopPropagation();
        if (onClose) onClose(true);
        break;
      case 'Tab':
        if (open && onClose) onClose(false);
        break;
      default:
    }
  }, [open, active, count, edge, step, onPick, onClose, onOpen]);

  const itemRef = useCallback((i) => (el) => {
    if (el) els.current.set(i, el); else els.current.delete(i);
  }, []);

  return { active, setActive, onKeyDown, itemRef };
}

/** Đóng khi bấm/chạm ra ngoài các vùng `refs` (mảng ref). */
export function useOutsideClose(open, refs, onClose) {
  const cb = useRef(onClose);
  cb.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const inside = refs.some((r) => r.current && r.current.contains(e.target));
      if (!inside) cb.current();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

export const MOBILE_QUERY = '(max-width: 639px)';

/**
 * Vị trí danh sách thả xuống (position: fixed) bám theo ô neo `anchorRef`.
 *  - Hết chỗ phía dưới thì mở lên trên; rộng tối thiểu 240px (ô hẹp vẫn đọc được).
 *  - Màn hình hẹp: tấm nổi ở ĐẦU vùng nhìn thấy (visualViewport) — bàn phím ảo mở ra không che danh sách.
 * Trả về `pos` (null khi đóng) — chỉ vẽ danh sách khi đã có `pos` để không chớp sai chỗ.
 */
export function usePopupPos(open, anchorRef, popupRef, minWidth = 240) {
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const vv = window.visualViewport;
    const vw = window.innerWidth;
    let next;
    if (window.matchMedia && window.matchMedia(MOBILE_QUERY).matches) {
      const top = (vv ? vv.offsetTop : 0) + 8;
      const h = (vv ? vv.height : window.innerHeight) - 16;
      next = { mobile: true, top, maxH: Math.max(180, Math.min(h, 460)) };
    } else {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const below = vh - r.bottom - 8;
      const above = r.top - 8;
      const up = below < 220 && above > below;
      const width = Math.min(Math.max(r.width, minWidth), vw - 16);
      const left = Math.max(8, Math.min(r.left, vw - width - 8));
      next = {
        mobile: false,
        top: up ? undefined : r.bottom + 4,
        bottom: up ? vh - r.top + 4 : undefined,
        left,
        width,
        maxH: Math.max(160, Math.min(340, up ? above : below)),
      };
    }
    setPos((p) => (p && Object.keys(next).every((k) => p[k] === next[k]) ? p : next));
  }, [anchorRef, minWidth]);

  useLayoutEffect(() => {
    if (open) place(); else setPos(null);
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    let raf = 0;
    const onMove = (e) => {
      if (e && e.target && popupRef.current && popupRef.current.contains && popupRef.current.contains(e.target)) return; // cuộn trong danh sách
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(place);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const vv = window.visualViewport;
    if (vv) { vv.addEventListener('resize', onMove); vv.addEventListener('scroll', onMove); }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      if (vv) { vv.removeEventListener('resize', onMove); vv.removeEventListener('scroll', onMove); }
    };
  }, [open, place, popupRef]);

  return pos;
}
